import { useCallback, useState, useEffect, useMemo } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
} from '@/utils/tour-styles'

// Constants for timing delays
const TOUR_DELAYS = {
    SCROLL_WAIT: 300, // Wait for scroll animation to complete
    START_DELAY: 500, // Initial delay before starting tour
} as const

// Process status type
type ProcessStatus =
    | 'draft'
    | 'pending_approval'
    | 'approved'
    | 'completed'
    | 'cancelled'

interface TourStep {
    selector: string
    title: string
    description: string
    conditionBadge?: string
    // Status conditions: show step only when process is in these statuses
    // If undefined, step is shown for all statuses
    showForStatus?: ProcessStatus[]
}

interface UseProcessDetailTourProps {
    status?: ProcessStatus
}

interface UseProcessDetailTourReturn {
    startTour: () => void
    endTour: () => void
    isActive: boolean
    currentTourStep: number
}

/**
 * Tour steps configuration for warehouse process detail page
 * Covers process information, approval workflow, and tabs
 * Steps can be filtered based on process status using showForStatus
 */
const TOUR_STEPS: TourStep[] = [
    {
        selector: '[data-tour="process-detail-page"]',
        title: 'Chi tiết Process',
        description:
            'Trang chi tiết hiển thị đầy đủ thông tin process bao gồm: thông tin cơ bản, quy trình phê duyệt, danh sách items và lịch sử hoạt động.',
    },
    {
        selector: '[data-tour="process-detail-info"]',
        title: 'Thông tin Process',
        description:
            'Hiển thị thông tin chi tiết: mã process, loại, trạng thái, nguồn/đích, lý do, ghi chú và thông tin phê duyệt.',
    },
    // Draft status steps - guide to submit for approval
    {
        selector: '[data-tour="process-detail-header"]',
        title: 'Các hành động Draft',
        description:
            'Process đang ở trạng thái Draft. Bạn có thể: Submit (gửi phê duyệt), Edit (chỉnh sửa), Cancel (hủy), hoặc Delete (xóa).',
        conditionBadge: 'Draft',
        showForStatus: ['draft'],
    },
    {
        selector: '[data-tour="process-detail-header"]',
        title: 'Gửi phê duyệt',
        description:
            'Click nút "Submit" để gửi process lên chờ phê duyệt. Sau khi submit, process sẽ chuyển sang trạng thái "Pending Approval" và chờ Tech Lead & Storekeeper duyệt.',
        conditionBadge: 'Draft → Submit',
        showForStatus: ['draft'],
    },
    {
        selector: '[data-tour="process-detail-header"]',
        title: 'Chỉnh sửa Process',
        description:
            'Click nút "Edit" để chỉnh sửa thông tin process: thay đổi nguồn/đích, thêm/xóa items, cập nhật lý do và ghi chú.',
        conditionBadge: 'Draft',
        showForStatus: ['draft'],
    },
    // Approval action steps - only show for pending_approval status
    {
        selector: '[data-tour="process-approval-actions"]',
        title: 'Hành động phê duyệt',
        description:
            'Khu vực thực hiện phê duyệt hoặc từ chối process. Bạn có thể duyệt hoặc từ chối với vai trò Tech Lead hoặc Storekeeper.',
        conditionBadge: 'Pending Approval',
        showForStatus: ['pending_approval'],
    },
    {
        selector: '[data-tour="approval-actions-card"]',
        title: 'Form phê duyệt',
        description:
            'Chọn vai trò (Tech Lead hoặc Storekeeper), thêm ghi chú nếu cần, sau đó click Approve để duyệt hoặc Reject để từ chối.',
        conditionBadge: 'Pending Approval',
        showForStatus: ['pending_approval'],
    },
    {
        selector: '[data-tour="approval-role-selector"]',
        title: 'Chọn vai trò *',
        description:
            'Chọn vai trò của bạn: Tech Lead (phụ trách kỹ thuật) hoặc Storekeeper (thủ kho). Mỗi vai trò chỉ được phê duyệt một lần.',
        conditionBadge: 'Pending Approval',
        showForStatus: ['pending_approval'],
    },
    {
        selector: '[data-tour="approval-status-summary"]',
        title: 'Trạng thái phê duyệt',
        description:
            'Hiển thị trạng thái phê duyệt của từng vai trò. Tick xanh = đã duyệt, X xám = chưa duyệt. Cần cả 2 vai trò duyệt để hoàn thành.',
        conditionBadge: 'Pending Approval',
        showForStatus: ['pending_approval'],
    },
    {
        selector: '[data-tour="approval-comments"]',
        title: 'Ghi chú phê duyệt',
        description:
            'Thêm ghi chú hoặc nhận xét khi phê duyệt/từ chối. Ghi chú sẽ được lưu trong lịch sử.',
        conditionBadge: 'Không bắt buộc',
        showForStatus: ['pending_approval'],
    },
    {
        selector: '[data-tour="approval-buttons"]',
        title: 'Nút hành động',
        description:
            'Click "Approve" (màu xanh) để phê duyệt hoặc "Reject" (màu đỏ) để từ chối. Khi từ chối, bạn cần nhập lý do.',
        conditionBadge: 'Pending Approval',
        showForStatus: ['pending_approval'],
    },
    // Tab steps - show for all statuses
    {
        selector: '[data-tour="process-tabs"]',
        title: 'Các tab thông tin',
        description:
            'Chuyển đổi giữa các tab: Items (danh sách thiết bị), Approval Progress (tiến độ phê duyệt), Activity Logs (lịch sử hoạt động).',
    },
    {
        selector: '[data-tour="process-tab-content"]',
        title: 'Nội dung tab',
        description:
            'Khu vực hiển thị nội dung của tab đang chọn. Mặc định hiển thị tab Items với danh sách thiết bị trong process.',
    },
    {
        selector: '[data-tour="approval-progress-card"]',
        title: 'Tiến độ phê duyệt',
        description:
            'Bảng hiển thị các bước phê duyệt: Tạo mới → Tech Lead duyệt → Storekeeper duyệt → Hoàn thành. Mỗi bước có trạng thái và thời gian.',
        conditionBadge: 'Tab Approval Progress',
    },
    {
        selector: '[data-tour="approval-history-card"]',
        title: 'Lịch sử phê duyệt',
        description:
            'Bảng ghi lại toàn bộ lịch sử phê duyệt: ai duyệt, hành động gì, thời gian nào, và ghi chú kèm theo.',
        conditionBadge: 'Tab Approval Progress',
    },
]

/**
 * Tour hook for Warehouse Process Detail page
 * Guides users through the process management workflow
 *
 * @param props.status - Current process status to filter relevant tour steps
 *
 * Usage:
 * - Press Ctrl+Shift+H to start the tour
 * - Navigate using Enter/Arrow keys or click buttons
 * - Press Escape to close
 *
 * Tour steps are filtered based on process status:
 * - pending_approval: Shows all steps including approval actions
 * - draft/approved/completed/cancelled: Skips approval action steps
 */
export const useProcessDetailTour = (
    props: UseProcessDetailTourProps = {}
): UseProcessDetailTourReturn => {
    const { status } = props
    const [isActive, setIsActive] = useState(false)
    const [currentTourStep, setCurrentTourStep] = useState(0)

    // Filter tour steps based on process status
    // Steps without showForStatus are shown for all statuses
    // Steps with showForStatus are only shown if current status is in the array
    const tourSteps = useMemo(() => {
        return TOUR_STEPS.filter((step) => {
            // If no status filter defined, show for all statuses
            if (!step.showForStatus) return true
            // If status is not provided, show all steps (fallback to element check)
            if (!status) return true
            // Check if current status is in the allowed statuses
            return step.showForStatus.includes(status)
        })
    }, [status])

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentTourStep(0)
    }, [])

    const showTourUI = useCallback(
        (step: TourStep, stepIndex: number) => {
            injectTourStyles()
            cleanupTourElements()
            createTourOverlay()

            const element = document.querySelector(step.selector) as HTMLElement
            if (!element) {
                // Log warning for debugging in development
                if (process.env.NODE_ENV === 'development') {
                    console.warn(
                        `[Tour] Element not found: ${step.selector} - skipping step ${stepIndex + 1}`
                    )
                }
                // Skip to next step if element not found
                if (stepIndex < tourSteps.length - 1) {
                    // Recursive call with next index
                    const nextStep = tourSteps[stepIndex + 1]
                    if (nextStep) {
                        setCurrentTourStep(stepIndex + 1)
                        setTimeout(
                            () => showTourUI(nextStep, stepIndex + 1),
                            TOUR_DELAYS.SCROLL_WAIT
                        )
                    }
                } else {
                    endTour()
                }
                return
            }

            element.classList.add('tour-highlight-element')
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })

            // Wait for scroll then add popover
            setTimeout(() => {
                const goToNextStep = () => {
                    if (stepIndex === tourSteps.length - 1) {
                        endTour()
                    } else {
                        const nextStep = tourSteps[stepIndex + 1]
                        if (nextStep) {
                            cleanupTourElements()
                            setCurrentTourStep(stepIndex + 1)
                            showTourUI(nextStep, stepIndex + 1)
                        }
                    }
                }

                const goToPrevStep = () => {
                    const prevStep = tourSteps[stepIndex - 1]
                    if (prevStep) {
                        cleanupTourElements()
                        setCurrentTourStep(stepIndex - 1)
                        showTourUI(prevStep, stepIndex - 1)
                    }
                }

                const popover = createTourPopover({
                    title: step.title,
                    description: step.description,
                    currentStep: stepIndex + 1,
                    totalSteps: tourSteps.length,
                    onPrev: stepIndex > 0 ? goToPrevStep : undefined,
                    onNext: goToNextStep,
                    onClose: endTour,
                    isLastStep: stepIndex === tourSteps.length - 1,
                    conditionBadge: step.conditionBadge,
                })

                document.body.appendChild(popover)
                positionPopover(popover, element)
            }, TOUR_DELAYS.SCROLL_WAIT)
        },
        [tourSteps, endTour]
    )

    const startTour = useCallback(() => {
        setIsActive(true)
        setCurrentTourStep(0)

        window.scrollTo({ top: 0, behavior: 'smooth' })

        setTimeout(() => {
            const firstStep = tourSteps[0]
            if (firstStep) {
                showTourUI(firstStep, 0)
            }
        }, TOUR_DELAYS.START_DELAY)
    }, [tourSteps, showTourUI])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupTourElements()
        }
    }, [])

    return { startTour, endTour, isActive, currentTourStep }
}
